import React from 'react';
import styled from 'styled-components';

import media from '../../../utils/mediaQueries';
import {
  colors, radius, spacing,
} from '../tokens';
import { DemarcheSituationOption } from '../types';

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.md};
  margin: ${spacing.lg} 0;

  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

const OptionCard = styled.label<{ isSelected: boolean }>`
  display: block;
  position: relative;
  padding: ${spacing.md};
  border: 1px solid ${({ isSelected }) => (isSelected ? colors.brand : colors.border)};
  border-radius: ${radius.medium};
  background-color: ${({ isSelected }) => (isSelected ? colors.brandSoft : colors.surface)};
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${colors.brand};
  }

  &:focus-within {
    box-shadow: 0 0 0 3px ${colors.brandMedium};
  }
`;

const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const OptionLabel = styled.span`
  display: block;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: ${colors.text};
  margin-bottom: ${spacing.xs};
`;

const OptionDescription = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.45;
  color: ${colors.textMuted};
`;

type SituationOptionGroupProps = {
  name: string;
  ariaLabel: string;
  options: DemarcheSituationOption[];
  selectedValue: string | null;
  onSelect: (value: string) => void;
};

const SituationOptionGroup: React.FC<SituationOptionGroupProps> = ({
  name,
  ariaLabel,
  options,
  selectedValue,
  onSelect,
}) => (
  <Options role="radiogroup" aria-label={ariaLabel}>
    {options.map((option) => (
      <OptionCard key={option.value} isSelected={selectedValue === option.value}>
        <HiddenRadio
          type="radio"
          name={name}
          value={option.value}
          checked={selectedValue === option.value}
          onChange={() => onSelect(option.value)}
        />
        <OptionLabel>{option.label}</OptionLabel>
        {option.description && <OptionDescription>{option.description}</OptionDescription>}
      </OptionCard>
    ))}
  </Options>
);

export { SituationOptionGroup };
